import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import lightBoxHandler from './LightBoxHandler';
import LightBoxToolbar from './LightBoxToolbar';
import LightBoxImageArea from './LightBoxImageArea';
import LightBoxFooter from './LightBoxFooter';
import QuestionDlg from '../../../components/shared/QuestionDlg/QuestionDlg';
import './LightBox.css';

/**
 * LightBox — fullscreen image viewer.
 * State only — all logic delegated to LightBoxHandler.
 * @param {object} props
 * @param {object[]} props.documents - image-only documents
 * @param {number} props.initialIndex - index to open at
 * @param {Function} props.onClose
 */
export default function LightBox({ documents: initialDocuments, initialIndex, onClose }) {
    // Local copy — mutated on delete so the lightbox updates without waiting for the strip
    const [documents, setDocuments] = useState(initialDocuments);
    const [index, setIndex] = useState(initialIndex ?? 0);
    const [scale, setScale] = useState(1);
    // Local rotation state so UI updates immediately without waiting for BE
    const [rotation, setRotation] = useState(initialDocuments[initialIndex ?? 0]?.rotation ?? 0);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const doc = documents[index];

    // Keyboard navigation — Escape, ArrowLeft, ArrowRight
    useEffect(() => {
        const onKey = (e) => lightBoxHandler.handleKey(e, setIndex, documents.length, onClose);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [index, documents.length]);

    // Reset zoom and restore rotation from doc on image change
    useEffect(() => {
        setScale(1);
        setRotation(documents[index]?.rotation ?? 0);
    }, [index, documents]);

    if (!doc) return null;

    const handleConfirmDelete = () => {
        setDeleteConfirmOpen(false);
        lightBoxHandler.handleDelete(doc, documents, index, setDocuments, setIndex, onClose);
    };

    return (
        <Box id="lightbox-overlay" className="lightbox-overlay" onClick={(e) => lightBoxHandler.handleBackdropClick(e, onClose)}>
            <Box id="lightbox-panel" className="lightbox-panel">
                <LightBoxToolbar
                    fileName={doc.fileName}
                    rotation={rotation}
                    onRotateCCW={() => lightBoxHandler.handleRotateCCW(doc, rotation, setRotation)}
                    onRotateCW={() => lightBoxHandler.handleRotateCW(doc, rotation, setRotation)}
                    onDownload={() => lightBoxHandler.handleDownload(doc)}
                    onDelete={() => setDeleteConfirmOpen(true)}
                    onClose={onClose}
                />
                <LightBoxImageArea
                    doc={doc}
                    rotation={rotation}
                    scale={scale}
                    index={index}
                    total={documents.length}
                    onPrev={() => lightBoxHandler.handlePrev(setIndex)}
                    onNext={() => lightBoxHandler.handleNext(setIndex, documents.length)}
                    onWheel={(e) => lightBoxHandler.handleWheel(e, setScale)}
                />
                <LightBoxFooter
                    fileName={doc.fileName}
                    description={doc.description}
                />
            </Box>

            <QuestionDlg
                open={deleteConfirmOpen}
                title="Delete Attachment"
                message={`Are you sure you want to delete "${doc.fileName}"? This cannot be undone.`}
                buttons={[
                    { label: 'Cancel', onClick: () => setDeleteConfirmOpen(false) },
                    { label: 'Delete', onClick: handleConfirmDelete, color: '#ff6b6b' },
                ]}
            />
        </Box>
    );
}
